import React from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { SpaSettingsLabels } from "../../../mqttData/settings/spaSettingsLabels";
import { SpaConstLabels } from "../../../mqttData/consts/spaConstsLabels.enums";
import { Tooltip } from "@mui/material";
import { ChangesIcon, } from "../../../assets/svg/svg";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { SpecGroup, SpecRow, hasData } from "./SpecGroup";
import { isV1Spa } from "../../../hooks/useSpaCommand";
import { palName } from "../../../utils/brand";

const SpaCard: React.FC<{editMode:any, newSettings:any,setNewSettings:any}> = ({editMode, newSettings, setNewSettings}) => {
    const spa = useSelector((state: RootState) => state.spa.data);
    // Spa data and settings:
    const settingsData = useSelector((state: RootState) => state.settings.data);
    const constData = useSelector((state: RootState) => state.const.data);
    const {data:summary, loading:summaryLoading} = useSelector((state: RootState) => state.fieldHistorySummary);
    const {canAccess: canSeeChanges} = usePermissionCheck(['can_access_dev_controls']);
    const isV1 = isV1Spa(spa);
    const show = (value: unknown) => !isV1 || hasData(value);

  return (
    <section className="sd-section sd-spec-section sd-spa">
      <div className="sd-specs">
        <SpecGroup>
          <SpecRow label="GUID" mono>{spa?.id ?? '-'}</SpecRow>
          <SpecRow label="SN">{spa.blue_falls_serial_number ?? spa.arctic_serial_number ?? "N/A"}</SpecRow>
          <SpecRow label="Nickname">{spa?.nick_name ?? 'n/a'}</SpecRow>
          {isV1 && settingsData.v1ProductCode && <SpecRow label="Product Code">{settingsData.v1ProductCode}</SpecRow>}
        </SpecGroup>

        <SpecGroup>
          {show(settingsData[SpaSettingsLabels.YOCFWVer] ?? spa?.yocto_version) && <SpecRow label="Yocto">{settingsData[SpaSettingsLabels.YOCFWVer] ?? spa?.yocto_version ?? '--'}</SpecRow>}
          {show(settingsData[SpaSettingsLabels.LPCFWVer]) && <SpecRow label="LPC">{settingsData[SpaSettingsLabels.LPCFWVer] ?? '--'}</SpecRow>}
          {show(settingsData[SpaSettingsLabels.SBFWVer]) && <SpecRow label={palName}>{settingsData[SpaSettingsLabels.SBFWVer] ?? '--'}</SpecRow>}
        </SpecGroup>

        <SpecGroup>
          {show(settingsData[SpaSettingsLabels.TAG1]) && <SpecRow label="RFIDa">{settingsData[SpaSettingsLabels.TAG1] ?? '--'}</SpecRow>}
          {show(settingsData[SpaSettingsLabels.TAG2]) && <SpecRow label="RFIDb">{settingsData[SpaSettingsLabels.TAG2] ?? '--'}</SpecRow>}
          {show(settingsData.RFIDConnected) && <SpecRow label="RFIDComs">
            <span className={`sd-pill ${settingsData.RFIDConnected ? 'good' : 'bad'}`}>
              {settingsData.RFIDConnected ? 'Connected' : 'Disconnected'}
            </span>
          </SpecRow>}
        </SpecGroup>

        <SpecGroup>
          {show(settingsData[SpaSettingsLabels.cfgToff]) && <SpecRow label="Temp.">
            {editMode ?
              <><input id="Toff" type="number" defaultValue={settingsData[SpaSettingsLabels.cfgToff]??0} min={constData[SpaConstLabels.cfgToffmin]??-5} max={constData[SpaConstLabels.cfgToffmax]??5}
                onChange={(e)=>setNewSettings({...newSettings, cfgToff: Number(e.target.value)})}/> °F</>
              :
              `${settingsData[SpaSettingsLabels.cfgToff] ?? 0}°F`}
          </SpecRow>}
          {show(settingsData[SpaSettingsLabels.cfgORPoff]) && <SpecRow label="ORP">
            {editMode ?
              <><input id="ORPoff" type="number" defaultValue={(settingsData[SpaSettingsLabels.cfgORPoff] ?? 0)*10} min={(constData[SpaConstLabels.cfgORPoffmin]??-20)*10} max={(constData[SpaConstLabels.cfgORPoffmax]??20)*10}
                onChange={(e)=>setNewSettings({...newSettings, cfgORPoff: Math.round(Number(e.target.value)/10)})} step={50}/> mV</>
              :
              `${(settingsData[SpaSettingsLabels.cfgORPoff] ?? 0)*10} mV`}
          </SpecRow>}
          {show(settingsData[SpaSettingsLabels.cfgpHoff]) && <SpecRow label="pH">
            {editMode ?
              <input id="pHoff" type="number" step={0.1} defaultValue={(settingsData[SpaSettingsLabels.cfgpHoff] ?? 0)/10} min={(constData[SpaConstLabels.cfgpHoffmin]??-20)/10} max={(constData[SpaConstLabels.cfgpHoffmax]??20)/10}
                onChange={(e)=>setNewSettings({...newSettings, cfgpHoff: Math.round(Number(e.target.value)*10)})}/>
              :
              (settingsData[SpaSettingsLabels.cfgpHoff] ?? 0)/10}
          </SpecRow>}
        </SpecGroup>

        <SpecGroup>
          {show(settingsData.log_level) && <SpecRow label="Log Level">{settingsData.log_level ?? "INFO"}</SpecRow>}
          {canSeeChanges &&
            <SpecRow label="Changes">
                    {(summary??[]).length>0 ?
                    summaryLoading ?
                        <span className="spinner sd-spinner-sm" role="progressbar" aria-label="Loading"/>
                    :
                    <Tooltip 
                    classes={{ tooltip: 'changes-tooltip-custom' }}
                    title={
                            <div className="changes-tooltip">  
                                {summary.map((item:any, i:number) => (
                                    <div key={item.id} className={`changes-item ${item.field_name === 'dealership_id' ? 'dealership' : 'user'}`}>
                                        <h3>{item.field_name === 'dealership_id' ? 'Dealership' : 'User'}</h3>
                                        
                                        <div className="changes-detail">
                                            <span className="label">Total Changes: <span className="value">{item.total_changes}</span></span>
                                        </div>
                                        
                                        <div className="changes-detail">
                                            <span className="label">Initial:</span>
                                            <span className={`value ${!item.initial_entity ? 'null' : 'unchanged'}`}>
                                                {item.initial_entity 
                                                    ? (item.field_name === 'dealership_id' 
                                                        ? `${item.initial_entity.name} (${item.initial_entity.id})`
                                                        : `${item.initial_entity.name} (${item.initial_entity.email})`)
                                                    : 'No assignment'
                                                }
                                            </span>
                                        </div>
                                        
                                        <div className="changes-detail">
                                            <span className="label">Current:</span>
                                            <span className={`value ${item.last_entity ? 'changed' : 'null'}`}>
                                                {item.last_entity 
                                                    ? (item.field_name === 'dealership_id' 
                                                        ? `${item.last_entity.name} (${item.last_entity.id})`
                                                        : `${item.last_entity.name} (${item.last_entity.email})`)
                                                    : 'No assignment'
                                                }
                                            </span>
                                        </div>
                                        
                                        <div className="changes-detail">
                                            <span className="label">First Change:</span>
                                            <span className="value">{new Date(item.first_change).toLocaleString()}</span>
                                        </div>
                                        
                                        <div className="changes-detail">
                                            <span className="label">Last Change:</span>
                                            <span className="value">{new Date(item.last_change).toLocaleString()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        } arrow placement="bottom" enterTouchDelay={0}>
                            <div>
                                <ChangesIcon/>
                            </div>
                        </Tooltip>
                    :
                    <div>-</div>
                    }
            </SpecRow>
          }
        </SpecGroup>
      </div>
    </section>
  );
};

export default SpaCard;
